import React from "react";
import { useQuery } from "@tanstack/react-query";
import { growthMetrics } from "@/lib/api";
import { CurrencyDollar, TrendUp, Users, Funnel, EnvelopeSimple, ShareNetwork, Robot, Trophy } from "@phosphor-icons/react";

const Kpi = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="card-matte p-4" data-testid={`metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="flex items-center gap-1.5 label-caps text-[10px]"><Icon size={12} className={accent || "brand-text"} /> {label}</div>
    <div className="font-head text-2xl font-extrabold mt-1.5">{value}</div>
    {sub && <div className="text-[10px] text-[#7a7a7a] mt-0.5">{sub}</div>}
  </div>
);

export const GrowthMetrics = () => {
  const { data: m, isLoading } = useQuery({ queryKey: ["growth-metrics"], queryFn: growthMetrics });
  if (isLoading || !m) return <div className="text-sm text-[#7a7a7a] py-10 text-center">Loading metrics…</div>;
  const r = m.revenue, f = m.funnel;
  const maxG = Math.max(1, ...m.workspaces.growth_series.map((g) => g.new_workspaces));
  return (
    <div data-testid="growth-metrics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi label="MRR" value={`$${r.mrr.toLocaleString()}`} sub={`ARR $${r.arr.toLocaleString()}`} icon={CurrencyDollar} />
        <Kpi label="Paying Customers" value={r.paying_customers} sub={`${r.trials} on trial`} icon={Users} />
        <Kpi label="Churn (30d)" value={`${r.churn_30d_pct}%`} sub={`${r.canceled_30d} canceled`} icon={TrendUp}
          accent={r.churn_30d_pct > 5 ? "text-red-400" : "text-emerald-400"} />
        <Kpi label="Demo Requests" value={f.demo_requests_total} sub={`${f.demo_requests_30d} in last 30 days`} icon={Funnel} />
        <Kpi label="Demos Scheduled" value={f.demos_scheduled} sub="scheduled + completed" icon={Funnel} />
        <Kpi label="Lead → Customer" value={`${f.lead_to_customer_pct}%`} sub={`${f.customers_from_leads} converted`} icon={Trophy} />
        <Kpi label="AI Usage This Month" value={m.ai_usage_month.credits} sub={`${m.ai_usage_month.actions} AI actions · credits`} icon={Robot} />
        <Kpi label="Workspaces" value={m.workspaces.total} sub="total businesses on platform" icon={Users} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-matte p-5" data-testid="metric-customer-growth">
          <div className="label-caps mb-3">Customer growth — new workspaces</div>
          <div className="flex items-end gap-2 h-28">
            {m.workspaces.growth_series.map((g) => (
              <div key={g.month} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-[#cfcfcf] font-semibold">{g.new_workspaces}</span>
                <div className="w-full rounded-t bg-[#6B3DFF]" style={{ height: `${(g.new_workspaces / maxG) * 80}px`, minHeight: 3 }} />
                <span className="text-[9px] text-[#5a5a5a]">{g.month.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card-matte p-5" data-testid="metric-channel-performance">
          <div className="label-caps mb-3">Channel performance — last 30 days</div>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-[#9a9a9a]"><EnvelopeSimple size={15} className="text-amber-300" /> Email</span>
              <span className="text-[#cfcfcf]"><b className="text-white">{m.email_30d.published}</b> sent · {m.email_30d.created} created</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-[#9a9a9a]"><ShareNetwork size={15} className="text-sky-400" /> Social</span>
              <span className="text-[#cfcfcf]"><b className="text-white">{m.social_30d.published}</b> published · {m.social_30d.created} created</span>
            </div>
          </div>
        </div>
        <div className="card-matte p-5" data-testid="metric-top-campaigns">
          <div className="label-caps mb-3">Top campaigns — by published items</div>
          <div className="space-y-2">
            {m.top_campaigns.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="h-5 w-5 rounded-full bg-[#6B3DFF]/15 brand-text font-bold flex items-center justify-center text-[10px] shrink-0">{i + 1}</span>
                <span className="flex-1 truncate text-[#cfcfcf]">{c.name}<span className="text-[#5a5a5a]"> · {c.client}</span></span>
                <span className="text-white font-semibold shrink-0">{c.published}</span>
              </div>
            ))}
            {!m.top_campaigns.length && <div className="text-xs text-[#5a5a5a]">No published campaigns yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};
